import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// useReadingBooks — shelf hook for /reading. Returns the viewer's own
// books only; cross-pair visibility is reserved for an optional
// "their shelf" toggle in a follow-up PR. Realtime subscription so
// edits from the partner's device or another tab show up live.
export function useReadingBooks() {
  const { user } = useAuth()
  const userId = user?.id || null
  const [books, setBooks] = useState([])
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    if (!userId) { setBooks([]); return }
    const { data } = await supabase
      .from('reading_books')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
    setBooks(data || [])
  }, [userId])

  useEffect(() => {
    if (!userId) { setLoading(false); return undefined }
    let alive = true
    setLoading(true)
    refetch().then(() => { if (alive) setLoading(false) })

    const channel = supabase
      .channel(`reading-${userId}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'reading_books', filter: `user_id=eq.${userId}` },
        () => { if (alive) refetch() },
      )
      .subscribe()
    return () => { alive = false; supabase.removeChannel(channel) }
  }, [userId, refetch])

  const createBook = useCallback(async ({ title, author, status, cover_url, file_format, file_name, file_size }) => {
    if (!userId) return { error: 'not signed in' }
    const text = (title || '').trim()
    if (!text) return { error: 'title required' }
    const { data, error } = await supabase
      .from('reading_books')
      .insert({
        user_id: userId,
        title: text,
        author: (author || '').trim() || null,
        status: status || 'wishlist',
        cover_url: (cover_url || '').trim() || null,
        file_format: file_format || null,
        file_name: file_name || null,
        file_size: file_size ?? null,
      })
      .select('id')
      .single()
    if (error) return { error: error.message }
    await refetch()
    return { id: data.id }
  }, [userId, refetch])

  return { books, loading, refetch, createBook }
}

// useReadingBook(bookId) — detail hook with realtime + mutators.
// Returns isMine for the editing gate; viewing partner books is fine
// but they're read-only from this user's perspective.
export function useReadingBook(bookId) {
  const { user } = useAuth()
  const userId = user?.id || null
  const [book, setBook] = useState(null)
  const [highlights, setHighlights] = useState([])
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    if (!userId || !bookId) { setBook(null); setHighlights([]); return }
    const { data: b } = await supabase
      .from('reading_books')
      .select('*')
      .eq('id', bookId)
      .maybeSingle()
    setBook(b || null)
    if (b) {
      const { data: h } = await supabase
        .from('reading_highlights')
        .select('*')
        .eq('book_id', bookId)
        .order('created_at', { ascending: false })
      setHighlights(h || [])
    } else {
      setHighlights([])
    }
  }, [userId, bookId])

  useEffect(() => {
    if (!userId || !bookId) { setLoading(false); return undefined }
    let alive = true
    setLoading(true)
    refetch().then(() => { if (alive) setLoading(false) })

    const channel = supabase
      .channel(`reading-book-${bookId}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'reading_books', filter: `id=eq.${bookId}` },
        () => { if (alive) refetch() },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'reading_highlights', filter: `book_id=eq.${bookId}` },
        () => { if (alive) refetch() },
      )
      .subscribe()
    return () => { alive = false; supabase.removeChannel(channel) }
  }, [userId, bookId, refetch])

  const isMine = !!book && book.user_id === userId

  const updateBook = useCallback(async (patch) => {
    if (!bookId || !isMine) return { error: 'not yours' }
    const safe = { ...patch }
    // Stamp finished_at when transitioning into 'finished' so the
    // dates section auto-populates. Don't clear it on other status
    // transitions — re-opening a finished book is rare and keeping
    // the historical finish date is more useful than losing it.
    if (safe.status === 'finished' && book && book.status !== 'finished' && !safe.finished_at) {
      safe.finished_at = new Date().toISOString().slice(0, 10)
    }
    if (safe.status === 'reading' && book && book.status !== 'reading' && !safe.started_at && !book.started_at) {
      safe.started_at = new Date().toISOString().slice(0, 10)
    }
    const { error } = await supabase.from('reading_books').update(safe).eq('id', bookId)
    if (error) return { error: error.message }
    await refetch()
    return {}
  }, [bookId, isMine, book, refetch])

  const deleteBook = useCallback(async () => {
    if (!bookId || !isMine) return { error: 'not yours' }
    const { error } = await supabase.from('reading_books').delete().eq('id', bookId)
    if (error) return { error: error.message }
    return {}
  }, [bookId, isMine])

  const addHighlight = useCallback(async (body, page = null) => {
    if (!bookId || !userId || !isMine) return { error: 'cannot highlight' }
    const text = (body || '').trim()
    if (!text) return { error: 'empty' }
    const { error } = await supabase.from('reading_highlights').insert({
      book_id: bookId,
      user_id: userId,
      body: text,
      page_number: page,
    })
    if (error) return { error: error.message }
    await refetch()
    return {}
  }, [bookId, userId, isMine, refetch])

  const deleteHighlight = useCallback(async (id) => {
    if (!id) return
    await supabase.from('reading_highlights').delete().eq('id', id)
    await refetch()
  }, [refetch])

  return {
    book,
    highlights,
    loading,
    isMine,
    updateBook,
    deleteBook,
    addHighlight,
    deleteHighlight,
    refetch,
  }
}
